import { useState, useEffect, useCallback } from 'react';
import api from '../api/client';

const useSeasonalityCoefficients = (marketplace = 'amazon') => {
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(''); 
  const [niches, setNiches] = useState([]); // [{ niche_category_id, niche_name, coefficients: [12 numbers] }]
  const [originalNiches, setOriginalNiches] = useState([]); // for cancel

  // ---------- fetch niches and coefficients ----------
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    setSuccessMessage('');
    try {
      // 1. fetch all niches
      const { data: nichesJson } = await api.get('/api/seasonality-niches');
      if (!nichesJson.success) throw new Error(nichesJson.error || 'Failed to fetch niches');
      const nicheList = nichesJson.data;

      // 2. fetch existing coefficients
      const { data: coeffJson } = await api.get(`/api/seasonality-coefficients?marketplace=${marketplace}`);
      if (!coeffJson.success) throw new Error(coeffJson.error || 'Failed to fetch coefficients');

      // 3. merge: for each niche, default all 12 months to 1.00, then override with DB values
      const merged = nicheList.map(niche => {
        const coefficients = new Array(12).fill(1.00); // index 0 = Jan, 11 = Dec
        coeffJson.data
          .filter(c => c.niche_category_id === niche.niche_category_id)
          .forEach(c => {
            coefficients[c.month_num - 1] = parseFloat(c.seasonality_coefficient);
          });
        return {
          ...niche,
          coefficients,
        };
      });

      setNiches(merged);
      // deep copy for reset
      setOriginalNiches(JSON.parse(JSON.stringify(merged)));
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  }, [marketplace]);

  // ---------- update a single coefficient locally ----------
  const updateCoefficient = (nicheId, monthIndex, value) => {
  setNiches(prev =>
    prev.map(niche => {
      if (niche.niche_category_id !== nicheId) return niche;
      const newCoefficients = [...niche.coefficients];
      newCoefficients[monthIndex] = value; // store raw string
      return { ...niche, coefficients: newCoefficients };
    })
  );
};

  // ---------- bulk apply coefficients from an uploaded file ----------
  // map: { [niche_category_id]: array of up to 12 values (null/'' = keep current) }
  const applyBulkCoefficients = (map) => {
    setNiches(prev => prev.map(n => {
      const upd = map[n.niche_category_id];
      if (!upd) return n;
      const coefficients = n.coefficients.map((c, i) =>
        upd[i] != null && upd[i] !== '' && !isNaN(parseFloat(upd[i])) ? parseFloat(upd[i]) : c
      );
      return { ...n, coefficients };
    }));
    setSuccessMessage('');
    setError(null);
  };

  // ---------- add a new niche (same endpoint as the General page) ----------
  // Appends it locally with 1.00 coefficients so unsaved edits are kept.
  const addNiche = async (name) => {
    const trimmed = (name || '').trim();
    if (!trimmed) return false;
    setError(null);
    setSuccessMessage('');
    try {
      const { data: json } = await api.post('/api/niches-batch', { names: [trimmed] });
      const item = json.items && json.items[0];
      if (item) {
        const entry = {
          niche_category_id: item.niche_category_id,
          niche_name: item.niche_name,
          coefficients: new Array(12).fill(1),
        };
        setNiches(prev => [...prev, entry]);
        setOriginalNiches(prev => [...prev, entry]);
        setSuccessMessage(`Niche "${item.niche_name}" added — set its coefficients and press "Save All Changes"`);
      }
      return true;
    } catch (err) {
      setError(err.response ? (err.response.data?.error || 'Failed to add niche') : err.message);
      return false;
    }
  };

  // ---------- save all changes ----------
  const saveChanges = async () => {
    setSaving(true);
    setError(null);
    setSuccessMessage('');
    try {
      // Prepare payload – parse everything to numbers with 2 decimals
      const updates = niches.flatMap(niche =>
        niche.coefficients.map((coeff, idx) => {
          let num = parseFloat(coeff);
          if (isNaN(num)) num = 0;
          return {
            niche_category_id: niche.niche_category_id,
            month_num: idx + 1,
            seasonality_coefficient: num.toFixed(2),
          };
        })
      );

      const { data: json } = await api.post('/api/seasonality-coefficients', { updates, marketplace });
      if (!json.success) throw new Error(json.error || 'Save failed');

      // After successful save – round displayed values to 2 decimals (as numbers)
      const updatedNiches = niches.map(niche => ({
        ...niche,
        coefficients: niche.coefficients.map(coeff => {
          let num = parseFloat(coeff);
          if (isNaN(num)) num = 0;
          return parseFloat(num.toFixed(2)); // store as number
        })
      }));

      setNiches(updatedNiches);
      setOriginalNiches(JSON.parse(JSON.stringify(updatedNiches)));
      setSuccessMessage(`Seasonality coefficients saved successfully (${marketplace})`);
    } catch (err) {
      setError(err.response ? (err.response.data?.error || 'Save failed') : err.message);
    } finally {
      setSaving(false);
    }
  };


  // ---------- cancel changes: revert to original ----------
  const cancelChanges = () => {
    setNiches(JSON.parse(JSON.stringify(originalNiches)));
    setError(null);
    setSuccessMessage('');
  };

  // ---------- popup controls ----------
  const openPopup = () => {
    setIsPopupOpen(true);
    fetchData();
  };
  const closePopup = () => {
    setIsPopupOpen(false);
    setError(null);
    setSuccessMessage('');
    // reset to original after close? we can keep changes until cancel or save
  };

  // initial fetch when popup opens is handled in openPopup

  return {
    isPopupOpen,
    openPopup,
    closePopup,
    loading,
    saving,
    error,
    successMessage,
    niches,
    updateCoefficient,
    applyBulkCoefficients,
    addNiche,
    saveChanges,
    cancelChanges,
  };
};

export default useSeasonalityCoefficients;